import { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box, Flex, Text, Button, Textarea, Select, Icon, Spinner, Alert, AlertIcon, useToast,
} from '@chakra-ui/react';
import { LuUpload, LuPlus, LuArrowLeft, LuSparkles } from 'react-icons/lu';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  listProcesses, searchMovements, parseMovement, postMovement, fetchAccounts,
} from '../../api/endpointsBackend';
import SourceFilesUploader from '../source-files/SourceFilesUploader';
import SourceFileProcessingPipeline from '../source-files/SourceFileProcessingPipeline';
import useIsPhone from '../../hooks/useIsPhone';

const POLL_MS = 3000;
// An import runs 30 s to ~2.5 min in practice, almost all of it extraction and
// the categoriser. This is the ceiling before we stop watching and say so
// rather than spin forever.
const POLL_TIMEOUT_MS = 5 * 60 * 1000;

// The card's content column. The mockup keeps this step narrower than the card
// — a closing screen with two choices reads better than one spanning 680px.
const COL = '440px';

// Alerts on the app's own tints rather than Chakra's palette: a problem on the
// red tint, a note on the accent fill.
const ALERT_TINT = {
  error: { bg: 'rgba(185, 28, 28, 0.08)', _dark: { bg: 'rgba(248, 113, 113, 0.14)' } },
  info: { bg: 'redesign.bg.accent' },
};

/** Does the workspace have any movement at all? One day's window is enough to
 *  answer it, and it's the cheapest question the search endpoint takes. */
async function hasAnyMovement() {
  try {
    const res = await searchMovements([], null, null, null, 1, 0);
    const groups = res?.groups || res?.results || res;
    if (Array.isArray(groups)) return groups.length > 0;
    return !!res?.total;
  } catch (e) {
    return false;
  }
}

function InlineBox({ children }) {
  const isPhone = useIsPhone();
  return (
    <Box
      borderWidth="1px"
      borderColor="redesign.border"
      borderRadius="14px"
      p={isPhone ? '16px' : '18px'}
      mb="20px"
      maxW={isPhone ? 'none' : COL}
      bg={isPhone ? 'redesign.bg.surface' : undefined}
    >
      {children}
    </Box>
  );
}

/**
 * The closing step: get the user's first data in, then hand them to Movements.
 *
 * Whichever way they choose, the step waits for the thing that actually matters
 * — a movement existing — instead of trusting the call that started it. An
 * upload returns long before the pipeline has produced anything, and a
 * statement can import cleanly and still hold nothing at all.
 */
export default function OnboardingFinish({ onDone, onBack, defaultAccountId }) {
  const { t } = useTranslation('onboarding');
  const navigate = useNavigate();
  const isPhone = useIsPhone();
  const toast = useToast();
  // Where a failed import says so: brought into view when it happens, since
  // on a short phone it lands below the fold.
  const failureRef = useRef(null);

  const [mode, setMode] = useState('choose');   // choose | upload | movement
  const [process, setProcess] = useState(null);
  const [watching, setWatching] = useState(false);
  const [outcome, setOutcome] = useState(null); // null | 'empty' | 'timeout'
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [accountId, setAccountId] = useState('');
  const pollRef = useRef(null);
  const startedAt = useRef(null);

  // Which account this lands in is the user's to see. The sentence rarely
  // names one — "50k almuerzo" names none — so without this the step picked
  // whichever account sorted first and charged the movement to it silently.
  useEffect(() => {
    if (mode !== 'movement') return;
    let cancelled = false;
    fetchAccounts()
      .then((rows) => {
        if (cancelled) return;
        const list = Array.isArray(rows) ? rows : [];
        setAccounts(list);
        setAccountId((prev) => {
          if (prev && list.some((a) => String(a.id) === String(prev))) return prev;
          const preferred = list.find((a) => a.id === defaultAccountId);
          return String((preferred || list[0])?.id ?? '');
        });
      })
      .catch(() => { if (!cancelled) setAccounts([]); });
    return () => { cancelled = true; };
  }, [mode, defaultAccountId]);

  // Replacing the entry: onboarding is over, so the back gesture from
  // Movements must not land on the finished "Listo" step again.
  const finish = useCallback(() => {
    onDone?.();
    navigate('/movements', { replace: true });
  }, [onDone, navigate]);

  // One watch at a time. Stopping bumps the generation, so a tick still in
  // flight from an earlier watch, from before Cancelar, or from after this
  // step unmounted neither acts on what it reads nor starts another interval.
  const watchGen = useRef(0);
  const alive = useRef(false);
  const stopPolling = () => {
    watchGen.current += 1;
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  };
  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; stopPolling(); };
  }, []);
  // What the user chose, readable from async code started before they chose.
  const modeRef = useRef(mode);
  modeRef.current = mode;

  // A failed import says so below the pipeline, which on a short phone is
  // below the fold. Bring it into view once the pipeline above it has
  // finished expanding (its Collapse animates), not on the tick that found it.
  useEffect(() => {
    if (!error || mode !== 'upload') return undefined;
    const id = setTimeout(() => {
      failureRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 400);
    return () => clearTimeout(id);
  }, [error, mode]);

  // Watch the import the uploader just started: which step it's on, whether it
  // failed, and — when it's done — whether it actually produced a movement.
  const watchImport = useCallback(async () => {
    stopPolling();
    const gen = watchGen.current;
    const current = () => alive.current && watchGen.current === gen;
    setWatching(true);
    setOutcome(null);
    setError(null);
    startedAt.current = Date.now();

    const tick = async () => {
      if (!current()) return;
      try {
        // The list, not the per-process status endpoint. Two reasons: it
        // answers { total, page, page_size, processes: [...] } — reading it as
        // an array is what left this spinning forever while the import had
        // already failed — and its rows carry the full `steps`, each with the
        // message of whatever went wrong. The status endpoint only carries a
        // `steps_summary` with no error text, which is the one thing a failed
        // run needs to say. Newest first, and onboarding has exactly one.
        const list = await listProcesses({ pageSize: 1, page: 1 });
        if (!current()) return;
        const latest = (list?.processes || [])[0];
        if (!latest) return; // not recorded yet; the next tick will find it
        setProcess(latest);

        if (latest?.status === 'completed') {
          stopPolling();
          setWatching(false);
          const hasMovement = await hasAnyMovement();
          if (!alive.current) return;
          if (hasMovement) finish();
          else setOutcome('empty');
          return;
        }
        if (latest?.status === 'failed') {
          stopPolling();
          setWatching(false);
          // Deliberately not the exception text: statement-process responses
          // redact it (`error_message` is always null, `error_code` only
          // names the kind of failure) so a bank file's contents cannot leak
          // back out.
          // See docs/specs/2026-09-13-bank-transactions-stay-server-side-design.md.
          // The pipeline above already marks which step stopped.
          setError(t('finish.upload.failed'));
          // The uploader's green "processing started" toast would still be
          // sitting on top of the failure it no longer describes.
          toast.closeAll();
          return;
        }
        if (Date.now() - startedAt.current > POLL_TIMEOUT_MS) {
          stopPolling();
          setWatching(false);
          setOutcome('timeout');
        }
      } catch (e) { /* keep polling; a blip is not a failure */ }
    };

    await tick();
    // The first tick may have ended the watch, or a newer one replaced it.
    if (current()) pollRef.current = setInterval(tick, POLL_MS);
  }, [finish, t, toast]);

  // Coming back to this step (a reload, or Atrás and then forward again) must
  // not lose what already happened: an import still running is picked up
  // where it is, and a workspace that already has a movement is done.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await listProcesses({ pageSize: 1, page: 1 });
        const latest = (list?.processes || [])[0];
        // The answer can arrive after the user already chose a way out:
        // their choice stands.
        if (cancelled || modeRef.current !== 'choose') return;
        if (latest && (latest.status === 'running' || latest.status === 'pending')) {
          setMode('upload');
          watchImport();
          return;
        }
      } catch (e) { /* nothing to pick up */ }
      const hasMovement = await hasAnyMovement();
      if (!cancelled && modeRef.current === 'choose' && hasMovement) finish();
    })();
    return () => { cancelled = true; };
    // Once, on arriving at the step.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submitMovement = async () => {
    if (!text.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const parsed = await parseMovement(text.trim());
      // The parser names an account only when the sentence does, and it
      // answers with a free-form integer — it has returned ids belonging to no
      // account here. Trust it only when it matches one actually on screen;
      // otherwise the movement goes where the user chose.
      const named = accounts.find((a) => a.id === parsed.account_id);
      const chosen = named ? named.id : Number(accountId);
      if (!chosen) {
        setError(t('finish.movement.noAccount'));
        return;
      }
      await postMovement({ ...parsed, account_id: chosen });
      finish();
    } catch (err) {
      // FastAPI answers a validation error with a list of objects in
      // `detail`, which cannot be rendered as text.
      const detail = err?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : t('finish.movement.error'));
    } finally {
      setBusy(false);
    }
  };

  const backToChoice = () => {
    stopPolling();
    setMode('choose'); setProcess(null); setWatching(false);
    setOutcome(null); setError(null);
  };

  const cta = {
    flex: '1', h: 'auto', px: '18px', py: '10px',
    fontSize: '14px', fontWeight: 600, borderRadius: '10px',
  };
  // The phone's buttons: 52px, as its other screens draw their actions.
  const phoneBtn = isPhone ? { h: '52px', fontSize: '17px', borderRadius: '14px' } : {};
  // The inline forms' small buttons. On the phone they are full width and
  // stacked: side by side, "Guardar movimiento" does not fit a 320px screen.
  const small = {
    h: 'auto', px: '12px', py: '7px', fontSize: '13px', fontWeight: 600, borderRadius: '9px',
    ...(isPhone && { w: '100%', ...phoneBtn, fontSize: '16px' }),
  };

  return (
    <Box>
      <Flex
        boxSize="56px"
        borderRadius="16px"
        align="center"
        justify="center"
        bg="rgba(21, 128, 61, 0.10)"
        _dark={{ bg: 'rgba(74, 222, 128, 0.14)' }}
        mb="18px"
      >
        <Icon as={LuSparkles} boxSize="26px" strokeWidth={1.6} color="redesign.amount.income" />
      </Flex>
      <Text fontSize="21px" fontWeight={700} letterSpacing="-0.02em" color="redesign.heading" mb="8px">
        {t('wizard.summary.title')}
      </Text>
      <Text fontSize="14px" lineHeight="1.6" color="redesign.text.muted" mb="24px" maxW={COL}>
        {t('wizard.summary.subtitle')}
      </Text>

      {/* Side by side the two don't fit a phone — "Agregar un movimiento"
          alone is wider than half of it — so there they stack, full width. */}
      {mode === 'choose' && (
        <Flex
          gap="12px"
          mb="20px"
          maxW={isPhone ? 'none' : COL}
          direction={isPhone ? 'column' : 'row'}
          // A card narrower than both labels (a 768px tablet) wraps them
          // instead of pushing itself past the page.
          flexWrap={isPhone ? undefined : 'wrap'}
        >
          <Button
            {...cta}
            {...(isPhone
              ? { variant: 'phonePrimary', flex: 'none', ...phoneBtn }
              // minW auto: sized from the label, so the row can wrap.
              : { variant: 'primary', minW: 'auto' })}
            leftIcon={<Icon as={LuUpload} boxSize={isPhone ? '20px' : '16px'} />}
            onClick={() => setMode('upload')}
          >
            {t('finish.upload.title')}
          </Button>
          <Button
            {...cta}
            {...phoneBtn}
            flex={isPhone ? 'none' : cta.flex}
            minW={isPhone ? undefined : 'auto'}
            variant="outline"
            borderColor="redesign.border.strong"
            color="redesign.text"
            bg="redesign.bg.surface"
            _hover={{ bg: 'redesign.bg.hover' }}
            leftIcon={<Icon as={LuPlus} boxSize={isPhone ? '20px' : '16px'} />}
            onClick={() => setMode('movement')}
          >
            {t('finish.movement.title')}
          </Button>
        </Flex>
      )}

      {mode === 'upload' && (
        <InlineBox>
          {!watching && !process && (
            <SourceFilesUploader
              onUploadStarted={watchImport}
              defaultAccountId={defaultAccountId}
            />
          )}

          {(watching || process) && (
            <Box>
              {watching && (
                <Flex align="center" gap={2} mb={3}>
                  <Spinner size="sm" color="redesign.accent.blue" />
                  <Text fontSize="13px" color="redesign.text.muted">{t('finish.upload.working')}</Text>
                </Flex>
              )}
              <SourceFileProcessingPipeline process={process} stepsDetails={process?.steps} />
            </Box>
          )}

          {error && (
            <Alert ref={failureRef} status="error" {...ALERT_TINT.error} borderRadius="lg" mt={4} alignItems="flex-start">
              <AlertIcon color="redesign.accent.red" />
              <Box>
                <Text fontSize="13px" fontWeight={700} color="redesign.heading" mb="2px">
                  {t('finish.upload.failedTitle')}
                </Text>
                <Text fontSize="13px">{error}</Text>
              </Box>
            </Alert>
          )}

          {outcome === 'empty' && (
            <Alert status="info" {...ALERT_TINT.info} borderRadius="lg" mt={4}>
              <AlertIcon color="redesign.accent.blue" />
              <Text fontSize="13px">{t('finish.upload.noMovements')}</Text>
            </Alert>
          )}
          {outcome === 'timeout' && (
            <Alert status="info" {...ALERT_TINT.info} borderRadius="lg" mt={4}>
              <AlertIcon color="redesign.accent.blue" />
              <Text fontSize="13px">{t('finish.upload.stillRunning')}</Text>
            </Alert>
          )}

          <Flex justify="flex-end" mt="12px">
            <Button
              {...small}
              variant="outline" borderColor="redesign.border.strong" color="redesign.text"
              bg="redesign.bg.surface" _hover={{ bg: 'redesign.bg.hover' }}
              onClick={backToChoice}
            >
              {t('finish.cancel')}
            </Button>
          </Flex>
        </InlineBox>
      )}

      {mode === 'movement' && (
        <InlineBox>
          <Textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={t('finish.movement.placeholder')}
            minH="88px"
            fontSize="14px"
            borderRadius="10px"
          />

          <Box mt="12px">
            <Text
              as="label"
              htmlFor="onboarding-movement-account"
              display="block"
              fontSize="13px"
              fontWeight={600}
              color="redesign.text"
              mb="6px"
            >
              {t('finish.movement.account')}
            </Text>
            <Select
              id="onboarding-movement-account"
              value={accountId}
              onChange={(e) => setAccountId(e.target.value)}
              h={isPhone ? '52px' : '38px'}
              fontSize="13px"
              borderRadius="9px"
            >
              {accounts.map((a) => (
                <option key={a.id} value={a.id}>{a.name}</option>
              ))}
            </Select>
          </Box>

          {error && (
            <Alert status="error" {...ALERT_TINT.error} borderRadius="lg" mt={3}>
              <AlertIcon color="redesign.accent.red" />
              <Text fontSize="13px">{error}</Text>
            </Alert>
          )}
          <Flex justify="flex-end" gap="8px" mt="12px" direction={isPhone ? 'column-reverse' : 'row'}>
            <Button
              {...small}
              variant="outline" borderColor="redesign.border.strong" color="redesign.text"
              bg="redesign.bg.surface" _hover={{ bg: 'redesign.bg.hover' }}
              onClick={backToChoice}
            >
              {t('finish.cancel')}
            </Button>
            <Button
              {...small}
              {...(isPhone
                ? { variant: 'phonePrimary' }
                : { variant: 'primary' })}
              onClick={submitMovement}
              isLoading={busy}
              isDisabled={!text.trim()}
            >
              {t('finish.movement.save')}
            </Button>
          </Flex>
        </InlineBox>
      )}

      {/* Nothing here is required — but leaving means leaving onboarding, and
          onboarding only ends when a movement exists. So the way out of this
          screen is backwards, never forwards. */}
      <Flex
        align="center"
        justify="flex-start"
        mt="24px"
        pt="20px"
        borderTopWidth="1px"
        borderTopColor="redesign.border"
      >
        <Button
          h="auto" px="12px" py="8px" fontSize="13px" fontWeight={600} borderRadius="10px"
          variant="ghost" color="redesign.text.muted"
          _hover={{ bg: 'redesign.bg.hover', color: 'redesign.text' }}
          leftIcon={<Icon as={LuArrowLeft} boxSize="16px" />}
          onClick={onBack}
        >
          {t('wizard.back')}
        </Button>
      </Flex>
    </Box>
  );
}
